import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { Loader } from "@/components/Loader";
import { PaywallGate } from "@/components/PaywallGate";
import { money } from "@/lib/format";
import { Wallet, TrendingUp, Users, CalendarDays, ShieldCheck } from "lucide-react";

const COLORS = ["#0F766E", "#059669", "#D97706", "#4F46E5", "#DC2626"];

export default function Analytics() {
  const { org } = useAuth();
  const cur = org?.currency || "NGN";
  const { data, isLoading } = useQuery({
    queryKey: ["analytics-overview"],
    queryFn: async () => (await api.get("/analytics/overview")).data,
  });
  if (isLoading) return <Loader full />;

  const revenueTrend = data?.revenue_trend?.length ? data.revenue_trend : [];
  const triage = data?.triage_breakdown?.length ? data.triage_breakdown : [];
  const apptStatus = data?.appointment_status?.length ? data.appointment_status : [];

  return (
    <PaywallGate>
      <PageHeader title="Analytics" subtitle={`${org?.name || "Organisation"} performance`} testId="page-analytics" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Wallet} label="Revenue" value={money(data?.revenue, cur)} tone="emerald" index={0} />
        <StatCard icon={TrendingUp} label="Outstanding" value={money(data?.outstanding, cur)} tone="amber" index={1} />
        <StatCard icon={Users} label="Total Patients" value={data?.total_patients ?? 0} tone="blue" index={2} />
        <StatCard icon={CalendarDays} label="Appointments" value={data?.total_appointments ?? 0} tone="indigo" index={3} />
        <StatCard icon={ShieldCheck} label="Claims Submitted" value={data?.claims_submitted ?? 0} tone="slate" index={4} />
        <StatCard icon={ShieldCheck} label="Claims Approved" value={data?.claims_approved ?? 0} tone="emerald" index={5} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h3 className="mb-4 font-display text-base font-semibold text-slate-900">Revenue trend</h3>
          {revenueTrend.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueTrend}>
                <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0F766E" stopOpacity={0.3} /><stop offset="95%" stopColor="#0F766E" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip />
                <Area type="monotone" dataKey="value" stroke="#0F766E" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-display text-base font-semibold text-slate-900">Triage breakdown</h3>
          {triage.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={triage} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={90} label>
                  {triage.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-display text-base font-semibold text-slate-900">Appointment status</h3>
        {apptStatus.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={apptStatus}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <Tooltip />
              <Bar dataKey="value" fill="#0F766E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </div>
    </PaywallGate>
  );
}

const Empty = () => <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">No data yet — activity will populate these charts.</div>;
